/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\customerCodesApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Class of Services module.
────────────────────────────────────────────────────────────────*/


import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type {
  CustomerCode,
  CreateCustomerCodePayload,
  UpdateCustomerCodePayload,
  ListCustomerCodesParams,
  ListCustomerCodesResponse,
} from '../types';

export async function fetchCustomerCodes(
  params: ListCustomerCodesParams,
  tenantSlug?: string
): Promise<ListCustomerCodesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    domainId: params.domainId.toString()
  });

  if (params.search) {
    queryParams.set('search', params.search);
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/customer-codes?${queryParams.toString()}`),
    { headers: getApiHeaders(tenantSlug) }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch customer codes');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch customer codes');
  }

  const data: CustomerCode[] = Array.isArray(result.data) ? result.data : [];
  const pagination = result.pagination || {};

  return {
    data,
    pagination: {
      page: pagination.page ?? params.page,
      limit: pagination.limit ?? params.limit,
      total: pagination.total ?? data.length,
      totalPages: pagination.totalPages ?? 0
    }
  };
}

export async function fetchCustomerCodeById(
  id: string | number,
  tenantSlug?: string
): Promise<CustomerCode> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/customer-codes/${id}`),
    { headers: getApiHeaders(tenantSlug) }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch customer code');
  }

  const result = await response.json();
  if (!result?.success || !result?.data) {
    throw new Error(result?.message || 'Failed to fetch customer code');
  }

  return result.data;
}

export async function createCustomerCode(
  payload: CreateCustomerCodePayload,
  tenantSlug?: string
): Promise<CustomerCode> {
  const response = await fetch(
    buildApiUrl('/api/telephony/customer-codes'),
    {
      method: 'POST',
      headers: getApiHeaders(tenantSlug),
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to create customer code');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create customer code');
  }

  return result.data || { ...payload, id: result.data?.id, uuid: result.data?.uuid };
}

export async function updateCustomerCode(
  id: string | number,
  payload: UpdateCustomerCodePayload,
  tenantSlug?: string
): Promise<CustomerCode> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/customer-codes/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(tenantSlug),
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update customer code');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update customer code');
  }

  return result.data || { ...payload, id: Number(id), uuid: result.data?.uuid };
}

export async function deleteCustomerCodeById(
  id: string | number,
  tenantSlug?: string
): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/customer-codes/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug),
    }
  );

  if (!response.ok) {
    let message = 'Failed to delete Customer Code';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
}

export async function exportCustomerCodes(
  tenantSlug?: string
): Promise<string> {
  const response = await fetch(
    buildApiUrl('/api/telephony/customer-codes/export'),
    { headers: getApiHeaders(tenantSlug) }
  );

  if (!response.ok) {
    throw new Error('Failed to export customer codes');
  }

  return response.text();
}

export async function importCustomerCodes(
  csvContent: string,
  mode: 'skip' | 'update' | 'replace',
  tenantSlug?: string
): Promise<{
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}> {
  const response = await fetch(
    buildApiUrl('/api/telephony/customer-codes/import'),
    {
      method: 'POST',
      headers: getApiHeaders(tenantSlug),
      body: JSON.stringify({
        csvContent,
        mode
      })
    }
  );

  const payload = await response.json();

  if (!response.ok || !payload?.success) {
    throw new Error(payload?.message || 'Failed to import customer codes');
  }

  return payload.data;
}

export async function fetchQuickListItems(
  domainId: number,
  tenantSlug?: string
): Promise<Array<{ id: number; code: string; description?: string | null; active: boolean }>> {
  const params = new URLSearchParams({
    page: '1',
    limit: '100',
    sortBy: 'code',
    sortOrder: 'asc',
    domainId: String(domainId),
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/customer-codes?${params.toString()}`),
    { headers: getApiHeaders(tenantSlug) }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch customer codes');
  }

  const result = await response.json();
  return Array.isArray(result?.data) ? result.data : [];
}

